import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "../../../../i18n/LanguageContext";
import DateField from "../../../common/datetime/DateField";
import {
  addDays,
  browserTimeZone,
  todayDate,
} from "../../../common/datetime/dateTimeParts";
import "../css/PauseModal.css";

/**
 * PauseModal Component
 *
 * Modal dialog for configuring pause settings for simulations.
 * Allows users to pause indefinitely, until a specific date, or for a duration.
 */
function PauseModal({ isOpen, onClose, onConfirm, initialValues = {} }) {
  const { t } = useTranslation();
  const [pauseOption, setPauseOption] = React.useState(
    initialValues.pauseOption || "indefinite",
  );
  const [endDate, setEndDate] = React.useState(initialValues.endDate || "");
  const [durationDays, setDurationDays] = React.useState(
    initialValues.durationDays || "30",
  );
  const [reason, setReason] = React.useState(initialValues.reason || "");

  // Pausing has to end in the future, so the earliest allowed day is tomorrow.
  const getMinDate = () => addDays(todayDate(browserTimeZone()), 1);

  const handleConfirm = () => {
    const pauseData = {
      pauseOption,
      endDate: pauseOption === "until-date" ? endDate : null,
      durationDays: pauseOption === "duration" ? parseInt(durationDays) : null,
      reason: reason.trim() || null,
    };

    onConfirm(pauseData);
  };

  const isValid = () => {
    if (pauseOption === "until-date" && !endDate) return false;
    if (
      pauseOption === "duration" &&
      (!durationDays || parseInt(durationDays) < 1)
    )
      return false;
    return true;
  };

  if (!isOpen) return null;

  return (
    <div className="pause-modal-overlay" onClick={onClose}>
      <div className="pause-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="pause-modal-header">
          <h2>{t("sps.pauseModal.title")}</h2>
          <button className="pause-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="pause-modal-body">
          <p className="pause-modal-description">
            {t("sps.pauseModal.description")}
          </p>

          <div className="pause-option-group">
            <label className="pause-option">
              <input
                type="radio"
                name="pauseOption"
                value="indefinite"
                checked={pauseOption === "indefinite"}
                onChange={(e) => setPauseOption(e.target.value)}
              />
              <div className="pause-option-content">
                <span className="pause-option-title">
                  {t("sps.pauseModal.optionIndefiniteTitle")}
                </span>
                <span className="pause-option-desc">
                  {t("sps.pauseModal.optionIndefiniteDesc")}
                </span>
              </div>
            </label>

            <label className="pause-option">
              <input
                type="radio"
                name="pauseOption"
                value="until-date"
                checked={pauseOption === "until-date"}
                onChange={(e) => setPauseOption(e.target.value)}
              />
              <div className="pause-option-content">
                <span className="pause-option-title">
                  {t("sps.pauseModal.optionUntilTitle")}
                </span>
                <span className="pause-option-desc">
                  {t("sps.pauseModal.optionUntilDesc")}
                </span>
              </div>
            </label>

            {pauseOption === "until-date" && (
              <div className="pause-option-input">
                <DateField
                  label={t("sps.pauseModal.optionUntilTitle")}
                  value={endDate}
                  min={getMinDate()}
                  required
                  presets={["tomorrow", "+7d", "+30d"]}
                  onChange={setEndDate}
                />
              </div>
            )}

            <label className="pause-option">
              <input
                type="radio"
                name="pauseOption"
                value="duration"
                checked={pauseOption === "duration"}
                onChange={(e) => setPauseOption(e.target.value)}
              />
              <div className="pause-option-content">
                <span className="pause-option-title">
                  {t("sps.pauseModal.optionDaysTitle")}
                </span>
                <span className="pause-option-desc">
                  {t("sps.pauseModal.optionDaysDesc")}
                </span>
              </div>
            </label>

            {pauseOption === "duration" && (
              <div className="pause-option-input">
                <div className="pause-duration-input-group">
                  <input
                    type="number"
                    value={durationDays}
                    onChange={(e) => setDurationDays(e.target.value)}
                    min="1"
                    max="365"
                    className="pause-duration-input"
                  />
                  <span className="pause-duration-label">
                    {t("sps.pauseModal.days")}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="pause-reason-group">
            <label htmlFor="pauseReason" className="pause-reason-label">
              {t("sps.pauseModal.reasonLabel")}
            </label>
            <textarea
              id="pauseReason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("sps.pauseModal.reasonPlaceholder")}
              className="pause-reason-textarea"
              rows="3"
              maxLength="255"
            />
            <span className="pause-reason-counter">
              {t("sps.pauseModal.reasonCounter").replace("{n}", reason.length)}
            </span>
          </div>
        </div>

        <div className="pause-modal-footer">
          <button className="pause-modal-button cancel" onClick={onClose}>
            {t("sps.pauseModal.cancel")}
          </button>
          <button
            className="pause-modal-button confirm"
            onClick={handleConfirm}
            disabled={!isValid()}
          >
            {t("sps.pauseModal.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PauseModal;
